import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChatTheme } from '@/types/theme';
import { Message } from '@/types/chat';
import { StreamingMessageBubble } from './StreamingMessageBubble';
import { format } from 'date-fns';

/**
 * Props for message group following OfferChat.ai TypeScript standards
 */
interface StreamingMessageGroupProps {
  messages: Array<Message>;
  streamingMessageIds?: Record<string, boolean>;
  theme?: ChatTheme;
  avatarUrl?: string | null;
  operatorName?: string | null;
  typingSpeed?: number;
  showTimestamp?: boolean;
  className?: string;
}

/**
 * A group of messages from the same sender with streaming support
 * Following OfferChat.ai UI component standards
 */
export function StreamingMessageGroup({
  messages,
  streamingMessageIds = {},
  theme,
  avatarUrl,
  operatorName = 'Assistant',
  typingSpeed = 400,
  showTimestamp = true,
  className = ''
}: StreamingMessageGroupProps) {
  if (!messages.length) return null;

  const sender = messages[0].sender;
  const isUser = sender === 'user';

  // Get initials for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const initials = isUser ? 'U' : getInitials(operatorName);

  // Format timestamp
  const formatTimestamp = (date: Date) => {
    return format(date, 'h:mm a');
  };

  return (
    <div
      className={`message-group ${isUser ? 'user-group' : 'bot-group'} ${className}`}
      style={{
        display: 'flex',
        marginBottom: '1rem',
        justifyContent: isUser ? 'flex-end' : 'flex-start'
      }}
    >
      {/* Avatar for bot messages */}
      {!isUser && (
        <div style={{ marginRight: '0.5rem' }}>
          <Avatar className="h-8 w-8">
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt={operatorName} />
            ) : (
              <AvatarFallback
                style={{
                  backgroundColor: theme?.avatarbackgroundcolor || 'rgba(0,0,0,0.1)',
                  color: theme?.bottextcolor || '#000000'
                }}
              >
                {initials}
              </AvatarFallback>
            )}
          </Avatar>
        </div>
      )}

      {/* Message content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          maxWidth: isUser ? '70%' : '85%',
          width: 'fit-content'
        }}
      >
        {/* Sender name for bot messages */}
        {!isUser && (
          <div
            className="sender-name"
            style={{
              fontSize: '0.75rem',
              marginBottom: '0.25rem',
              color: theme?.usertextcolor || '#666'
            }}
          >
            {operatorName}
          </div>
        )}

        {/* Message bubbles */}
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              marginBottom: '0.25rem',
              maxWidth: '100%',
              width: 'fit-content'
            }}
          >
            <StreamingMessageBubble
              text={message.text}
              isStreaming={!!streamingMessageIds[message.id]}
              sender={message.sender}
              theme={theme}
              typingSpeed={typingSpeed}
              timestamp={message.timestamp}
              messageId={message.id}
              metadata={message.metadata || {}}
            />
          </div>
        ))}

        {/* Timestamp */}
        {showTimestamp && messages.length > 0 && (
          <div
            className="timestamp"
            style={{
              fontSize: '0.7rem',
              marginTop: '0.25rem',
              color: theme?.usertextcolor || '#666'
            }}
          >
            {formatTimestamp(messages[messages.length - 1].timestamp)}
          </div>
        )}
      </div>

      {/* Avatar for user messages */}
      {isUser && (
        <div style={{ marginLeft: '0.5rem' }}>
          <Avatar className="h-8 w-8">
            <AvatarFallback
              style={{
                backgroundColor: theme?.primarycolor || '#007BFF',
                color: theme?.usertextcolor || '#FFFFFF'
              }}
            >
              U
            </AvatarFallback>
          </Avatar>
        </div>
      )}
    </div>
  );
}
